import React, { useState, useEffect, useCallback } from 'react';

interface QAPair {
  question: string;
  capability: string;
}

const qaPairs: QAPair[] = [
  {
    question: "What's a good rate for this lane right now?",
    capability: "ATLAS pulls live market data + lane history"
  },
  {
    question: "Post this load for me — here are the details.",
    capability: "ATLAS creates the load with clean, accurate form fill"
  },
  {
    question: "Is this load worth taking?",
    capability: "ATLAS analyzes rate, distance, backhaul, fuel, market trends"
  },
  {
    question: "Show me weather and risks along this route.",
    capability: "ATLAS delivers real-time insights so you can make faster, more confident decisions."
  },
  {
    question: "Find me verified trucks near the pickup.",
    capability: "ATLAS searches capacity with trust scores and filters"
  }
];

interface AtlasTypewriterProps {
  children: React.ReactNode;
}

const AtlasTypewriter: React.FC<AtlasTypewriterProps> = ({ children }) => {
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [displayedQuestion, setDisplayedQuestion] = useState('');
  const [showCapability, setShowCapability] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' 
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const currentPair = qaPairs[currentPairIndex];

  // Typewriter effect
  useEffect(() => {
    if (prefersReducedMotion) {
      // Skip animation for reduced motion
      setDisplayedQuestion(currentPair.question);
      setShowCapability(true);
      setIsTyping(false);
      return;
    }

    if (!isTyping) return;

    const question = currentPair.question;
    let charIndex = 0;
    setDisplayedQuestion('');
    setShowCapability(false);

    const typeInterval = setInterval(() => {
      if (charIndex < question.length) {
        setDisplayedQuestion(question.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        // Show capability after brief pause
        setTimeout(() => {
          setShowCapability(true);
        }, 500);
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [currentPairIndex, isTyping, currentPair.question, prefersReducedMotion]);

  // Cycle to next pair
  useEffect(() => {
    if (isTyping || !showCapability) return;

    // Hold for 3.5 seconds, then fade out and move to next
    const holdTimeout = setTimeout(() => {
      setIsFadingOut(true);
      
      // After fade out, move to next pair
      setTimeout(() => {
        setIsFadingOut(false);
        setCurrentPairIndex((prev) => (prev + 1) % qaPairs.length);
        setIsTyping(true);
        setShowCapability(false);
      }, 500);
    }, 3500);

    return () => clearTimeout(holdTimeout);
  }, [isTyping, showCapability]);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Question with typewriter effect */}
      <div 
        className={`h-8 sm:h-10 flex items-center justify-center transition-opacity duration-500 ${
          isFadingOut ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <span className="text-[#1ab7aa] italic text-lg sm:text-xl lg:text-2xl text-center">
          "{displayedQuestion}
          {isTyping && (
            <span className="animate-[typewriter-cursor_0.7s_ease-in-out_infinite]">|</span>
          )}
          {!isTyping && '"'}
        </span>
      </div>

      {/* Button slot */}
      {children}

      {/* Capability with fade-in effect */}
      <div 
        className={`h-8 sm:h-10 flex items-center justify-center transition-all duration-500 ${
          showCapability && !isFadingOut 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-2'
        }`}
      >
        <span className="text-gray-600 text-base sm:text-lg lg:text-xl text-center">
          {currentPair.capability}
        </span>
      </div>
    </div>
  );
};

export default AtlasTypewriter;
